import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { CommentFilterDropdownModule } from '../feedback-filters/comment-filter/comment-filter-dropdown.module';
import { MarkerFilterDropdownModule } from '../feedback-filters/marker-filter/marker-filter-dropdown.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GoMaterialModule } from '@go-modules/go-material.module';
import { FeedbackDisplayHeaderComponent } from '../feedback-display-header/feedback-display-header.component';
import { COMMENT_RESOURCE_TYPES } from 'go-modules/models/comment/comment.constant';
import { FeedbackSessionService } from '@go-modules/services/feedback-session/feedback-session.service';
import { map, Observable, take } from 'rxjs';
import * as dayjs from 'dayjs';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { FormatTimePipe } from '@go-modules/pipes/format-time/format-time.pipe';
import { GoPrintService } from '@go-modules/services/go-print/go-print.service';

@Component({
	selector: 'comment-tab-header',
	templateUrl: './comment-tab-header.component.html',
	styleUrls: ['./comment-tab-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		TranslateModule,
		GoMaterialModule,
		CommentFilterDropdownModule,
		MarkerFilterDropdownModule,
		FeedbackDisplayHeaderComponent
	],
	providers: [
		FormatTimePipe
	]
})
export class CommentTabHeaderComponent implements OnInit, OnDestroy {
	@Input() public feedbackSession;
	@Input() public rerunningAiFeedback: boolean;
	@Output() public rerunAiFeedback = new EventEmitter<void>();
	private resizeObserver: ResizeObserver;

	public showSyncEventCommentsFilterButton$: Observable<boolean> = this.ngxCommentListService.comments$.pipe(
		map((comments) => {
			return this.ngxCommentListService.filterOutSyncEventComments || comments.some((comment) => {
				return comment.resource?.some((res) => res.resource_type === COMMENT_RESOURCE_TYPES.SYNC_EVENT);
			});
		})
	);

	constructor (
		public feedbackSessionService: FeedbackSessionService,
		public ngxCommentListService: CommentListService,
		private formatTime: FormatTimePipe,
		private elementRef: ElementRef,
		private translate: TranslateService,
		private goPrintService: GoPrintService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) public userService: UserService
	) {}

	public ngOnInit () {
		this.observeSize();
	}

	public ngOnDestroy (): void {
		this.resizeObserver.disconnect();
	}

	public getAriaLabel (key: string): string {
		const labels = {
			'one-click-markers': 'feedback-session-feedback-display_comment-one-click-markers-label',
			'autoplay': 'feedback-session-feedback-display_comment-autoplay-label'
		};

		const baseLabel = this.translate.instant(labels[key]);
		if (!baseLabel) return '';

		const isEnabled = key === 'one-click-markers'
			? this.feedbackSessionService.getOneClickMarkerEnabled()
			: this.feedbackSessionService.getAutoPlayEnabled();

		const status = this.translate.instant(isEnabled ? 'common_switch-on' : 'common_switch-off');
		return `${baseLabel} ${status}`;
	}

	public showReRunAiFeedbackButton = (): boolean => {
		const activity = this.selectedService.getActivity();
		const session = this.selectedService.getSession();

		return this.userService.currentUser.is_root_user &&
			activity.ai_prompts_enabled &&
			session.media &&
			!session.media.isYoutube();
	};

	public printComments () {
		// with virtual scroll, not all content is rendered
		// instead create a new element to print
		const printElement = document.createElement('div');
		printElement.style.padding = '20px';
		this.ngxCommentListService.comments$.pipe(take(1)).subscribe((comments) => {
			comments.forEach((comment) => {
				const commentElement = document.createElement('div');
				let text = '';
				if (comment.time) {
					text += this.formatTime.transform(Number(comment.time)) + ' ';
				}
				text += `<b>${comment.first_name} ${comment.last_name}</b> ${dayjs(comment.created_at).format('MMM D, YYYY h:mmA')}: ${comment.text}`;
				commentElement.innerHTML = text;
				printElement.appendChild(commentElement);
			});
			this.goPrintService.print(
				printElement,
				'Printed Comments'
			);
		});
	}

	private observeSize () {
		const showOrHideTitle = (entries: ResizeObserverEntry[]) => {
			const subHeaderTitle: HTMLElement = this.elementRef.nativeElement.querySelector('.sub-header-title');
			if (subHeaderTitle) {
				if (entries[0].contentRect.width > 380) {
					subHeaderTitle.style.display = '';
				} else {
					subHeaderTitle.style.display = 'none';
				}
			}
		};

		this.resizeObserver = new ResizeObserver(showOrHideTitle);
		this.resizeObserver.observe(this.elementRef.nativeElement);
	}
}
